import { useEffect, useRef, useState } from 'react';
import { customerOrderPreviewResponseSchema, type CustomerOrder } from '@inktap/contracts/customer-orders';
import { request } from '../lib/api';

function OrderArtwork({ order }: { order: CustomerOrder }) {
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'unavailable' | 'error'>('idle');
  const [image, setImage] = useState<string | null>(null);
  const current = useRef<AbortController | null>(null);
  useEffect(() => () => current.current?.abort(), []);

  const load = async () => {
    if (current.current) return;
    const controller = new AbortController();
    current.current = controller;
    setState('loading');
    try {
      const payload = await request<unknown>(`/me/orders/${encodeURIComponent(order.id)}/preview`, {
        cache: 'no-store', signal: controller.signal
      });
      if (controller.signal.aborted) return;
      const parsed = customerOrderPreviewResponseSchema.safeParse(payload);
      if (!parsed.success || parsed.data.orderId !== order.id) throw new Error('Preview could not be verified');
      setImage(parsed.data.image);
      setState(parsed.data.image ? 'ready' : 'unavailable');
    } catch {
      if (!controller.signal.aborted) setState('error');
    } finally {
      if (current.current === controller) current.current = null;
    }
  };
  return (
    <div className="order-card__illustration">
      {state === 'ready' && image ? (
        <img className="order-card__artwork" src={image} alt={`Card design for order ${order.id}`}
          onError={() => { setImage(null); setState('error'); }} />
      ) : (
        <div className={`order-card__material order-card__material--${order.material.toLowerCase()}`} aria-hidden="true"><span>inktap</span></div>
      )}
      {state !== 'ready' ? <span>Illustration</span> : null}
      {state === 'unavailable' ? <span role="status">Preview unavailable</span> : null}
      {state === 'error' ? <span role="status">Could not load preview</span> : null}
      {state !== 'ready' && state !== 'unavailable' ? (
        <button type="button" className="order-card__preview-button" disabled={state === 'loading'}
          aria-label={`${state === 'error' ? 'Retry' : 'Show'} card preview for order ${order.id}`} onClick={() => void load()}>
          {state === 'loading' ? 'Loading...' : state === 'error' ? 'Retry preview' : 'Show preview'}
        </button>
      ) : null}
    </div>
  );
}
export { OrderArtwork };
